import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import RichTextEditor from '../components/RichTextEditor';
import { useAuth } from '../context/AuthContext';
import * as blogService from '../services/blogService';
import type { BlogPost } from '../services/blogService';

function formatDateLong(dateStr: string) {
  const date = new Date(dateStr);
  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'long' });
  const year = date.getFullYear();

  let suffix = 'th';
  if (day === 1 || day === 21 || day === 31) suffix = 'st';
  else if (day === 2 || day === 22) suffix = 'nd';
  else if (day === 3 || day === 23) suffix = 'rd';

  return `${month} ${day}${suffix}, ${year}`;
}

export default function BlogManagement(): React.ReactElement {
  const { token } = useAuth();

  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [formTitle, setFormTitle] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formExcerpt, setFormExcerpt] = useState('');
  const [formTags, setFormTags] = useState('');
  const [formFeaturedImage, setFormFeaturedImage] = useState('');
  const [formIsPublished, setFormIsPublished] = useState(false);
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const selectedPost = posts.find(p => p.id === selectedId) || null;

  useEffect(() => {
    loadPosts();
  }, [token]);

  async function loadPosts() {
    if (!token) return;
    try {
      setLoading(true);
      const data = await blogService.getAllPosts(token);
      setPosts(data);
      if (data.length > 0 && !selectedId) {
        setSelectedId(data[0].id);
      }
    } catch (err: any) {
      setError('Failed to load blog posts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  function parseTags(post: BlogPost): string[] {
    try {
      return JSON.parse(post.tags || '[]');
    } catch {
      return [];
    }
  }

  function openCreateForm() {
    setEditingPost(null);
    setFormTitle('');
    setFormContent('');
    setFormExcerpt('');
    setFormTags('');
    setFormFeaturedImage('');
    setFormIsPublished(false);
    setShowForm(true);
  }

  function openEditForm(post: BlogPost) {
    setEditingPost(post);
    setFormTitle(post.title);
    setFormContent(post.content);
    setFormExcerpt(post.excerpt || '');
    const tags = parseTags(post);
    setFormTags(tags.join(', '));
    setFormFeaturedImage(post.featured_image_url || '');
    setFormIsPublished(post.is_published);
    setShowForm(true);
  }

  async function handleSave() {
    if (!token || !formTitle.trim() || !formContent.trim()) return;
    try {
      setSaving(true);
      const tags = formTags
        .split(',')
        .map(t => t.trim().toLowerCase().replace(/^#/, ''))
        .filter(t => t.length > 0);

      if (editingPost) {
        const updated = await blogService.updateBlogPost(editingPost.id, {
          title: formTitle.trim(),
          content: formContent,
          excerpt: formExcerpt.trim() || undefined,
          tags,
          featured_image_url: formFeaturedImage.trim() || undefined,
          is_published: formIsPublished,
        }, token);
        setPosts(prev => prev.map(p => p.id === updated.id ? updated : p));
        setSelectedId(updated.id);
      } else {
        const created = await blogService.createBlogPost({
          title: formTitle.trim(),
          content: formContent,
          excerpt: formExcerpt.trim() || undefined,
          tags,
          featured_image_url: formFeaturedImage.trim() || undefined,
          is_published: formIsPublished,
        }, token);
        setPosts(prev => [created, ...prev]);
        setSelectedId(created.id);
      }
      setShowForm(false);
    } catch (err: any) {
      console.error('Failed to save blog post:', err);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!token || !deleteId) return;
    try {
      setDeleting(true);
      await blogService.deleteBlogPost(deleteId, token);
      setPosts(prev => {
        const filtered = prev.filter(p => p.id !== deleteId);
        if (selectedId === deleteId) {
          setSelectedId(filtered.length > 0 ? filtered[0].id : null);
        }
        return filtered;
      });
      setDeleteId(null);
    } catch (err: any) {
      console.error('Failed to delete blog post:', err);
    } finally {
      setDeleting(false);
    }
  }

  // Full-page create/edit form
  if (showForm) {
    return (
      <Layout>
        <div className="p-6">
          <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-white">
                  {editingPost ? 'Edit Blog Post' : 'Create Blog Post'}
                </h1>
                <p className="text-indigo-100 mt-1">
                  {editingPost ? 'Update your blog post content' : 'Write a new blog post for the public blog'}
                </p>
              </div>
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 bg-white/20 text-white rounded-lg font-medium hover:bg-white/30 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="p-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formTitle}
                  onChange={e => setFormTitle(e.target.value)}
                  placeholder="Enter blog post title..."
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              {/* Excerpt */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Excerpt <span className="text-gray-400 text-xs">(optional — shown in previews)</span>
                </label>
                <textarea
                  value={formExcerpt}
                  onChange={e => setFormExcerpt(e.target.value)}
                  placeholder="Brief summary of the post..."
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              {/* Featured Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Featured Image URL <span className="text-gray-400 text-xs">(optional — hero image displayed above the post)</span>
                </label>
                <input
                  type="text"
                  value={formFeaturedImage}
                  onChange={e => setFormFeaturedImage(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                {formFeaturedImage && (
                  <div className="mt-2 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                    <img
                      src={formFeaturedImage}
                      alt="Featured image preview"
                      className="w-full max-h-48 object-cover"
                      onError={e => (e.currentTarget.style.display = 'none')}
                    />
                  </div>
                )}
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tags <span className="text-gray-400 text-xs">(comma-separated, e.g. product updates, engineering, tips)</span>
                </label>
                <input
                  type="text"
                  value={formTags}
                  onChange={e => setFormTags(e.target.value)}
                  placeholder="product updates, engineering, tips"
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              {/* Published toggle */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setFormIsPublished(!formIsPublished)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                    formIsPublished ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      formIsPublished ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
                <div>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {formIsPublished ? 'Published' : 'Draft'}
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formIsPublished
                      ? 'Visible on the public blog'
                      : 'Only visible to you in this admin view'}
                  </p>
                </div>
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Content <span className="text-red-500">*</span>
                </label>
                <RichTextEditor
                  value={formContent}
                  onChange={setFormContent}
                  placeholder="Write your blog post here..."
                  minHeight="400px"
                  maxHeight="600px"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formTitle.trim() || !formContent.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? 'Saving...' : editingPost ? 'Update Post' : 'Create Post'}
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {/* Gradient Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Blog Management</h1>
              <p className="text-indigo-100 mt-1">Create and manage blog posts for the public blog</p>
            </div>
            <button
              onClick={openCreateForm}
              className="px-4 py-2 bg-white text-indigo-600 rounded-lg font-medium hover:bg-indigo-50 transition-colors"
            >
              New Blog Post
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg">
            {error}
          </div>
        ) : posts.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
            <svg className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">No blog posts yet</h3>
            <p className="text-gray-500 dark:text-gray-400">
              Create your first blog post to get started.
            </p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="flex">
              {/* Left sidebar */}
              <div className="w-72 shrink-0 border-r border-gray-200 dark:border-gray-700 p-4">
                <div className="space-y-1">
                  {posts.map(post => {
                    const tags = parseTags(post);
                    return (
                      <button
                        key={post.id}
                        onClick={() => setSelectedId(post.id)}
                        className={`w-full text-left px-3 py-2.5 rounded-md transition-colors text-sm ${
                          selectedId === post.id
                            ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <span className="font-medium line-clamp-2 block">{post.title}</span>
                        <span className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                            {post.published_at ? formatDateLong(post.published_at) : formatDateLong(post.created_at)}
                          </span>
                          {post.is_published ? (
                            <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                              Live
                            </span>
                          ) : (
                            <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded-full bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300">
                              Draft
                            </span>
                          )}
                        </span>
                        {tags.length > 0 && (
                          <span className="flex flex-wrap gap-1 mt-1.5">
                            {tags.slice(0, 3).map(tag => (
                              <span key={tag} className="px-1.5 py-0.5 text-[10px] rounded bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400">
                                #{tag}
                              </span>
                            ))}
                            {tags.length > 3 && (
                              <span className="px-1.5 py-0.5 text-[10px] text-gray-400">+{tags.length - 3}</span>
                            )}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Right content area */}
              <div className="flex-1 min-w-0 p-6">
                {selectedPost ? (
                  <div>
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                          {selectedPost.title}
                        </h2>
                        <div className="flex items-center gap-3 mt-2">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {selectedPost.published_at ? formatDateLong(selectedPost.published_at) : formatDateLong(selectedPost.created_at)}
                          </span>
                          {selectedPost.is_published ? (
                            <span className="px-2.5 py-1 text-xs font-semibold uppercase rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                              Published
                            </span>
                          ) : (
                            <span className="px-2.5 py-1 text-xs font-semibold uppercase rounded-full bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300">
                              Draft
                            </span>
                          )}
                          <span className="text-sm text-gray-400 dark:text-gray-500">
                            by {selectedPost.created_by_name}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-4">
                        <button
                          onClick={() => openEditForm(selectedPost)}
                          className="px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-full hover:bg-indigo-700 transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteId(selectedPost.id)}
                          className="px-4 py-1.5 text-sm font-medium text-white bg-red-600 rounded-full hover:bg-red-700 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    {/* Tags */}
                    {parseTags(selectedPost).length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-6">
                        {parseTags(selectedPost).map(tag => (
                          <span key={tag} className="px-2.5 py-1 text-xs font-medium rounded-full bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <div
                      className="prose dark:prose-invert max-w-none"
                      dangerouslySetInnerHTML={{ __html: selectedPost.content }}
                    />
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500 dark:text-gray-400">Select a blog post to view</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {deleteId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDeleteId(null)} />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Delete Blog Post</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Are you sure you want to delete this blog post? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteId(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
